import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { isPlatformServer } from '@angular/common';
import { of, interval } from 'rxjs';
import { take, catchError, tap, filter, switchMap, map } from 'rxjs/operators';

import { LocalStorageService } from './storage/local-storage.service';
import { PanelService } from './api/panel.service';
import { StoreDetailService } from './resolver/store-detail.service';
import { StoreCountry } from '../models/store-country.model';

const STORE_REGION_KEY = 'store-region';
const DEFAULT_REGION_CODE = 'GLC';

@Injectable({ providedIn: 'root' })
export class StoreRegionService {
  private localCountryList: StoreCountry[] = [];

  constructor(
    private localStorageService: LocalStorageService,
    private panelService: PanelService,
    private storeDetailService: StoreDetailService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  public async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<any> {
    return this.resolveInfo(route);
  }

  public get countryList(): StoreCountry[] { 
    return this.localCountryList;
  }

  public get selectedRegion(): StoreCountry { 
    return this.localStorageService.getItem(STORE_REGION_KEY);
  }

  public setRegion(region: StoreCountry) { 
    if (!region) return;
    this.localStorageService.setItem(STORE_REGION_KEY, region);
  }

  public setRegionById(id: number) {
    const region = this.getCountryByParam('id', id);
    if (!region) return;
    this.setRegion(region);
  }

  private async resolveInfo(route: ActivatedRouteSnapshot): Promise<any> {
    if (isPlatformServer(this.platformId)) return;

    await this.waitUntil(() => !!this.storeDetailService.permissionInit);

    await this.panelService.getStoreCountries({ ordering: 'name' })
      .pipe(
        tap((data) => {
          this.setCountryList(data);
          if (!this.selectedRegion) this.setDefaultRegion();
        }),
        take(1),
        catchError(() => of(null))
      )
      .toPromise();
  }

  private waitUntil(cond: () => boolean, timeoutMs = 8000, stepMs = 100): Promise<void> {
    const start = Date.now();
    return new Promise<void>((resolve, reject) => {
      const t = setInterval(() => {
        if (cond()) { clearInterval(t); resolve(); }
       else if (Date.now() - start > timeoutMs) { clearInterval(t); resolve(); } 
      }, stepMs);
    });
  }

  private setCountryList(list: StoreCountry[]) { 
    const globalIndex = list.findIndex(item => item.code === DEFAULT_REGION_CODE);
    if (globalIndex > -1) list.unshift(list.splice(globalIndex, 1)[0]);
    this.localCountryList = list;
  }

  private setDefaultRegion() {
    this.setRegion(this.getCountryByParam('is_country_of_request', true) || this.countryList[0]);
  }

  private getCountryByParam(field: keyof StoreCountry, value: any) { 
    return this.countryList?.find((item) => item[field] == value);
  }
}
